import { useEffect, useRef } from 'react';

/**
 * Auto-logout po N ms nieaktywności (Faza 88.md #3).
 * Aktywność = mousemove / mousedown / keydown / touchstart / scroll / wheel.
 * Timer resetuje się przy każdym wykrytym evencie; gdy minie idleMs bez aktywności
 * → wywołanie onIdle (typowo: logout()).
 *
 * Spec: 30 minut nieaktywności (WF-100/spec-bezpieczeństwa). Konfigurowalne przez idleMs.
 */
export function useIdleLogout(opts: {
  enabled: boolean;
  idleMs: number;
  onIdle: () => void;
}): void {
  const { enabled, idleMs, onIdle } = opts;
  const timerRef = useRef<number | null>(null);
  const onIdleRef = useRef(onIdle);

  // Trzymaj świeżą referencję do callbacka bez restartowania effectu
  useEffect(() => {
    onIdleRef.current = onIdle;
  }, [onIdle]);

  useEffect(() => {
    if (!enabled) return;

    const reset = (): void => {
      if (timerRef.current !== null) window.clearTimeout(timerRef.current);
      timerRef.current = window.setTimeout(() => onIdleRef.current(), idleMs);
    };

    const events: (keyof WindowEventMap)[] = [
      'mousedown',
      'mousemove',
      'keydown',
      'touchstart',
      'scroll',
      'wheel',
    ];

    events.forEach((ev) => window.addEventListener(ev, reset, { passive: true }));
    reset();

    return () => {
      events.forEach((ev) => window.removeEventListener(ev, reset));
      if (timerRef.current !== null) window.clearTimeout(timerRef.current);
    };
  }, [enabled, idleMs]);
}
